import { useEffect, useRef, useState } from 'react';
import { motion } from 'motion/react';

export function PlacementAnalysis({ onDone, durationMs = 5000, maxWaitMs = 8000 }) {
  const onDoneRef = useRef(onDone);
  const advancedRef = useRef(false);
  const [showManualContinue, setShowManualContinue] = useState(false);

  useEffect(() => {
    onDoneRef.current = onDone;
  }, [onDone]);

  useEffect(() => {
    const safeAdvance = () => {
      if (advancedRef.current) return;
      advancedRef.current = true;
      if (onDoneRef.current) onDoneRef.current();
    };

    const autoAdvanceTimer = setTimeout(safeAdvance, durationMs);
    const manualTimer = setTimeout(() => setShowManualContinue(true), durationMs);
    const hardStopTimer = setTimeout(safeAdvance, Math.max(maxWaitMs, durationMs + 3000));

    return () => {
      clearTimeout(autoAdvanceTimer);
      clearTimeout(manualTimer);
      clearTimeout(hardStopTimer);
    };
  }, [durationMs, maxWaitMs]);

  return (
    <motion.div
      className="flex min-h-full flex-col items-center justify-center px-6 py-8 text-center"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.35 }}
    >
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1, y: [0, -6, 0] }}
        transition={{ opacity: { duration: 0.25 }, scale: { duration: 0.25 }, y: { duration: 1.8, repeat: Infinity } }}
        className="mb-5 flex h-36 w-36 items-center justify-center rounded-full border-2 border-finley-primary/20 bg-finley-primary/10 shadow-finley-md"
      >
        <span className="text-7xl">🐬</span>
      </motion.div>

      <motion.p
        initial={{ opacity: 0, y: 6 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.5, duration: 0.3 }}
        className="mb-6 font-finley-display text-3xl text-finley-text"
      >
        Finley is building your reading adventure...
      </motion.p>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1, duration: 0.25 }}
        className="w-full max-w-xs"
      >
        <div className="relative mb-4 h-2 overflow-hidden rounded-finley-full bg-finley-primary/10">
          <motion.div
            className="h-full rounded-finley-full bg-gradient-to-r from-finley-primary/40 via-finley-accent/60 to-finley-secondary/60"
            initial={{ width: '0%' }}
            animate={{ width: '100%' }}
            transition={{ delay: 1, duration: 3, ease: 'easeInOut' }}
          />
        </div>
        <div className="flex justify-center gap-2">
          {Array.from({ length: 5 }).map((_, i) => (
            <motion.span
              key={i}
              className="h-2.5 w-2.5 rounded-full bg-finley-primary/35"
              animate={{ scale: [1, 1.5, 1], opacity: [0.35, 1, 0.35] }}
              transition={{ duration: 0.9, repeat: Infinity, delay: 1 + i * 0.2 }}
            />
          ))}
        </div>
        {showManualContinue && (
          <motion.button
            initial={{ opacity: 0, y: 4 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.25 }}
            type="button"
            onClick={() => {
              if (!advancedRef.current && onDoneRef.current) {
                advancedRef.current = true;
                onDoneRef.current();
              }
            }}
            className="mx-auto mt-5 inline-flex rounded-finley-full border border-finley-primary/25 bg-white/70 px-4 py-2 text-sm text-finley-text-light transition hover:border-finley-primary/45 hover:text-finley-text"
          >
            Tap to continue
          </motion.button>
        )}
      </motion.div>
    </motion.div>
  );
}
